// PostHog analytics — fire-and-forget wrapper.
//
// CEO-5: PostHog env vars (VITE_POSTHOG_KEY, VITE_POSTHOG_HOST) are wired
// at the infra layer (task #18) but no events were ever fired. This module
// exposes a tiny surface — `track()` and `identifyUser()` — that callers
// can drop anywhere without worrying about init order, network failures,
// or PostHog being absent in dev.
//
// Contract:
//   - NEVER throws. Every entry point is wrapped in try/catch.
//   - NEVER awaited. Calls return void; PostHog handles batching internally.
//   - No-ops when PostHog isn't loaded (no DSN, network blocked, etc).
//
// Why fire-and-forget: analytics must not block the user. A drunk thumb at
// a loud bar should never feel a 200ms hitch because PostHog's CDN was slow.
// The drink-log tap, KEEP, and end-night handlers all fire events DURING
// the success path — any async/blocking semantics would defeat the F2 spec.

import posthog from 'posthog-js'

// PostHog's runtime exposes `__loaded` as a boolean once init() resolves.
// The type is not in posthog-js's d.ts, so we narrow against the runtime
// shape — `as unknown` keeps strict-null happy without an `any`.
function isLoaded(): boolean {
  const ph = posthog as unknown as { __loaded?: boolean }
  return ph.__loaded === true
}

/**
 * Capture a PostHog event. Drops silently when PostHog isn't initialised
 * (dev, no DSN, network blocked). Never throws, never blocks.
 */
export function track(event: string, props?: Record<string, unknown>): void {
  try {
    if (!isLoaded()) return
    posthog.capture(event, props ?? {})
  } catch {
    // swallow — analytics must not break UX
  }
}

/**
 * Identify the current user. PostHog auto-merges the anonymous_id with the
 * supplied userId so pre-signup events stitch onto the same user profile.
 * Idempotent — safe to call on every page load.
 */
export function identifyUser(userId: string, traits?: Record<string, unknown>): void {
  try {
    if (!isLoaded()) return
    posthog.identify(userId, traits)
  } catch {
    // swallow — analytics must not break UX
  }
}
